/**
 * A deck's pages for a fit tool: each page at rest, and which parts show while
 * the deck moves from one page to the next. A part a deck builds shows across
 * the move into its page; a cue (an arrow, a tool, a tag) shows only at rest.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { PropertyValue } from '../../behaviors/tracebehavior';
import { Pages } from '../../behaviors/pages';
import { Properties } from '../../behaviors/properties';
import { interpolateValue } from '../../core/canopy/TransitionUtils';
import { holdingDeck } from '../../editor/pages/holdingDeck';
import { partsShown } from './scene';

/** How far into a move between two pages the deck is looked at: just before it arrives, past a cue shown only at rest. */
const NEAR = 0.9;

export interface DeckWalk<T> {
    /** What `atPage` returned on each page, at rest, in page order. */
    readonly pages: T[];
    /** The parts under the roots that show at some point of a move between two pages: what the deck builds. */
    readonly moving: ReadonlySet<SceneObject>;
}

/**
 * Show each page of `deck` at rest and call `atPage(k)` (0-based) there; pose the deck just before it arrives
 * at each page from the one before (the first page: just after it leaves for the second), as a transition
 * shows it, and note the parts under `roots` that show. The deck is left as it was (`holdingDeck`). A deck of
 * one page, or whose pages differ in no property value, moves nothing: every part it shows counts as moving.
 */
export async function walkDeck<T>(deck: SceneObject, roots: readonly SceneObject[], atPage: (k: number) => T): Promise<DeckWalk<T>> {
    const groups = [deck, ...deck.getDescendants()].flatMap(o => { const p = o.getBehavior(Properties); return p ? [p] : []; });
    const valuesNow = (): Map<Properties, Map<string, PropertyValue | undefined>> =>
        new Map(groups.map(p => [p, new Map(p.getPropertyNames().map(name => [name, p.getValue(name)]))]));
    return holdingDeck(deck, show => {
        const count = deck.getBehavior(Pages)!.getPageCount();
        const pages: T[] = [], moving = new Set<SceneObject>();
        let before: ReturnType<typeof valuesNow> | null = null, moved = false;
        for (let k = 0; k < count; k++) {
            show(k);
            pages.push(atPage(k));
            const now = valuesNow();
            for (const t of !before ? [] : k === 1 ? [1 - NEAR, NEAR] : [NEAR]) {
                // The values a transition from the page before shows at `t`, laid over this page's as it would.
                const laid: [Properties, string][] = [];
                try {
                    for (const [props, values] of now) {
                        for (const [name, to] of values) {
                            const from = before!.get(props)?.get(name);
                            if (from === undefined || to === undefined || JSON.stringify(from) === JSON.stringify(to)) continue;
                            props._setTransitionValue(name, interpolateValue(from, to, t));
                            laid.push([props, name]);
                        }
                    }
                    if (laid.length) moved = true;
                    for (const part of partsShown(roots)) moving.add(part);
                } finally {
                    for (const [props, name] of laid) props._clearTransitionValue(name);
                }
            }
            before = now;
        }
        if (!moved) {
            for (let k = 0; k < count; k++) { show(k); for (const part of partsShown(roots)) moving.add(part); }
        }
        return Promise.resolve({ pages, moving });
    });
}
